import React, { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { CheckCircle2, AlertCircle, Calendar } from "lucide-react";

export function ContactSection() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    role: "",
    notes: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [errors, setErrors] = useState([]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrors([]);

    try {
      const response = await axios.post("/api/v1/demo_requests", {
        demo_request: formData,
      });
      if (response.data.ok) {
        setSubmitSuccess(true);
        setFormData({
          name: "",
          email: "",
          phone: "",
          company: "",
          role: "",
          notes: "",
        });
      }
    } catch (error) {
      const messages =
        error.response?.data?.errors || ["Something went wrong. Please try again."];
      setErrors(messages);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="relative z-10 py-20 sm:py-24" aria-label="Book a Live Demo">
      <div className="mx-auto max-w-[1200px] px-4 sm:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Text */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="flex flex-col items-start max-w-lg"
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#4B73FF]/10 border border-[#4B73FF]/20 text-[#4B73FF] mb-6">
              <Calendar className="w-5 h-5" />
            </span>
            <h2 className="text-[2.1rem] font-bold leading-tight text-white tracking-tight sm:text-[2.6rem] lg:text-[3rem] mb-4">
              Experience the Clozdl.ai{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-b from-white to-[#4B73FF]">
                Difference
              </span>
            </h2>
            <p className="text-base text-white/55 leading-relaxed mb-8">
              Schedule a personalized walkthrough of the intelligence layer for real estate. We will
              demonstrate how Clozdl.ai handles Pre-Sales lead capture, Sales pipeline automation,
              and Post-Sales ledger collections mapped to your actual project timelines.
            </p>

            <div className="space-y-4">
              {[
                "Configured in 30 minutes for your team",
                "Integrates with 99acres, MagicBricks & WhatsApp",
                "Zero upfront configuration cost",
              ].map((text, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.1 * i }}
                  className="flex items-center gap-3"
                >
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-500/10 text-emerald-400">
                    <CheckCircle2 className="h-4 w-4" />
                  </span>
                  <span className="text-sm font-semibold text-white/80">{text}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Right Form Card */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="relative"
          >
            <div className="absolute -inset-2 rounded-3xl bg-gradient-to-br from-[#4B73FF]/10 to-transparent blur-xl" />

            <div className="relative bg-gradient-to-br from-white/5 to-white/[0.01] border border-white/10 rounded-2xl p-6 sm:p-10 shadow-[0_24px_48px_rgba(0,0,0,0.5)] backdrop-blur-md">
              {submitSuccess ? (
                <div className="text-center py-10 flex flex-col items-center">
                  <span className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500/10 text-emerald-400 mb-6 border border-emerald-500/20">
                    <CheckCircle2 className="w-8 h-8 animate-pulse" />
                  </span>
                  <h3 className="text-2xl font-bold text-white mb-3">Request Received!</h3>
                  <p className="text-white/60 text-sm leading-relaxed max-w-xs mx-auto mb-6">
                    Thank you for booking a demo. Our team will verify your details and reach out
                    within 2 hours.
                  </p>
                  <button
                    onClick={() => setSubmitSuccess(false)}
                    className="text-xs font-bold uppercase tracking-wider text-[#4B73FF] hover:underline"
                  >
                    Submit another request
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5" noValidate>
                  <div className="mb-6">
                    <h3 className="text-xl font-bold text-white">Book a Live Demo</h3>
                    <p className="text-xs text-white/40 mt-1">
                      Get custom pipelines set up for your organization
                    </p>
                  </div>

                  {errors.length > 0 && (
                    <div className="rounded-xl border border-rose-500/20 bg-rose-500/5 p-4 text-xs text-rose-400 flex gap-3">
                      <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
                      <ul className="list-disc pl-4 space-y-1">
                        {errors.map((msg, i) => (
                          <li key={i}>{msg}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label
                        htmlFor="form_name"
                        className="block text-xs font-semibold text-white/60 uppercase tracking-wide mb-2"
                      >
                        Name *
                      </label>
                      <input
                        id="form_name"
                        type="text"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Full Name"
                        className="w-full h-11 bg-white/5 border border-white/10 rounded-xl px-4 text-white text-sm focus:border-[#4B73FF] focus:outline-none placeholder:text-white/20 transition-colors"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="form_phone"
                        className="block text-xs font-semibold text-white/60 uppercase tracking-wide mb-2"
                      >
                        Phone Number *
                      </label>
                      <input
                        id="form_phone"
                        type="tel"
                        name="phone"
                        required
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="10 digit mobile number"
                        className="w-full h-11 bg-white/5 border border-white/10 rounded-xl px-4 text-white text-sm focus:border-[#4B73FF] focus:outline-none placeholder:text-white/20 transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="form_email"
                      className="block text-xs font-semibold text-white/60 uppercase tracking-wide mb-2"
                    >
                      Email Address *
                    </label>
                    <input
                      id="form_email"
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Your email address"
                      className="w-full h-11 bg-white/5 border border-white/10 rounded-xl px-4 text-white text-sm focus:border-[#4B73FF] focus:outline-none placeholder:text-white/20 transition-colors"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label
                        htmlFor="form_company"
                        className="block text-xs font-semibold text-white/60 uppercase tracking-wide mb-2"
                      >
                        Company Name
                      </label>
                      <input
                        id="form_company"
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        placeholder="Your company name"
                        className="w-full h-11 bg-white/5 border border-white/10 rounded-xl px-4 text-white text-sm focus:border-[#4B73FF] focus:outline-none placeholder:text-white/20 transition-colors"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="form_role"
                        className="block text-xs font-semibold text-white/60 uppercase tracking-wide mb-2"
                      >
                        Job Role
                      </label>
                      <input
                        id="form_role"
                        type="text"
                        name="role"
                        value={formData.role}
                        onChange={handleChange}
                        placeholder="Your job role"
                        className="w-full h-11 bg-white/5 border border-white/10 rounded-xl px-4 text-white text-sm focus:border-[#4B73FF] focus:outline-none placeholder:text-white/20 transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="form_notes"
                      className="block text-xs font-semibold text-white/60 uppercase tracking-wide mb-2"
                    >
                      Additional Notes
                    </label>
                    <textarea
                      id="form_notes"
                      name="notes"
                      rows="3"
                      value={formData.notes}
                      onChange={handleChange}
                      placeholder="Tell us about your active projects..."
                      className="w-full bg-white/5 border border-white/10 rounded-xl p-4 text-white text-sm focus:border-[#4B73FF] focus:outline-none placeholder:text-white/20 transition-colors resize-none"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full h-12 bg-gradient-to-r from-[#4B73FF] to-[#7c6fff] hover:opacity-90 active:scale-[0.98] text-white font-semibold rounded-xl text-sm transition-all shadow-[0_4px_14px_rgba(75,115,255,0.4)] disabled:opacity-50 flex items-center justify-center"
                  >
                    {isSubmitting ? "Submitting request..." : "Book My Demo"}
                  </button>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
